/**
 * DetectionIndicator - Shows detection status with icon and label
 *
 * Atomic level: Molecule
 * Used in: Regulatory Flags section to show the 5 heads test results for complaint detection
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../../lib/utils';
import IconWrapper from '../../../ui/IconWrapper';
import { FiCheck, FiX, FiHelpCircle } from 'react-icons/fi';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../ui/tooltip';
import type { DetectionStatus } from '../../../../types/record';

const indicatorVariants = cva(
  'flex items-center gap-2',
  {
    variants: {
      size: {
        sm: '',
        md: '',
        lg: '',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

const circleVariants = cva(
  'rounded-full flex items-center justify-center',
  {
    variants: {
      status: {
        detected: 'bg-[var(--go-success)]/10 border-2 border-[var(--go-success)]/40',
        not_detected: 'bg-[var(--go-bittersweet)]/10 border-2 border-[var(--go-bittersweet)]/40',
        uncertain: 'bg-[var(--go-warning)]/10 border-2 border-[var(--go-warning)]/40',
      },
      size: {
        sm: 'size-10',
        md: 'size-12',
        lg: 'size-14',
      },
    },
    defaultVariants: {
      status: 'detected',
      size: 'md',
    },
  }
);

const iconVariants = cva('', {
    variants: {
      status: {
      detected: 'text-[var(--go-success)]',
      not_detected: 'text-[var(--go-bittersweet)]',
      uncertain: 'text-[var(--go-warning)]',
      },
    size: {
      sm: 'size-5',
      md: 'size-6',
      lg: 'size-7',
    },
  },
  defaultVariants: {
    status: 'detected',
    size: 'md',
  },
});

const labelVariants = cva('font-medium text-foreground', {
  variants: {
    size: {
      sm: 'text-xs',
      md: 'text-xs',
      lg: 'text-sm',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

interface DetectionIndicatorProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof indicatorVariants> {
  label: string;
  status: DetectionStatus;
  explanation?: string;
}

const getStatusIcon = (status: DetectionStatus) => {
  switch (status) {
    case 'detected':
      return FiCheck;
    case 'not_detected':
      return FiX;
    case 'uncertain':
      return FiHelpCircle;
  }
};

const DetectionIndicator = React.forwardRef<HTMLDivElement, DetectionIndicatorProps>(
  ({ className, size, label, status, explanation, ...props }, ref) => {
    const Icon = getStatusIcon(status);

    const content = (
      <div
        ref={ref}
        className={cn(indicatorVariants({ size }), className)}
        {...props}
      >
        <div className={circleVariants({ status, size })}>
          <IconWrapper icon={Icon} className={iconVariants({ status, size })} />
        </div>
        <span className={labelVariants({ size })}>{label}</span>
      </div>
    );

    // If explanation exists, wrap in tooltip
    if (explanation) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p className="text-sm">{explanation}</p>
          </TooltipContent>
        </Tooltip>
      );
    }

    return content;
  }
);

DetectionIndicator.displayName = 'DetectionIndicator';

export { DetectionIndicator, indicatorVariants };
